// Reine Ableitungen des Abschnitts „Vertretung und Bevollmächtigter eines
// Kontaktes“. Bewusst ohne React: Die Regeln — was bei „Nein“ verschwindet, wie
// aus der Ankreuzliste Zeilen werden, wann der Freitextname trägt — sind das
// Fachliche daran und sollen ohne DOM prüfbar sein.

import { VERFUEGUNGS_ARTEN, verfuegungArtLabel } from "../../data/verfuegungsarten";
import { istVertretungsrolle, vertretungsrolleLabel } from "../../data/vertretungsrollen";
import { displayContactName } from "../../utils/contactDisplay";

/** Eine Verfügungszeile, wie sie im Formular-State und im PUT-Payload steht. */
export type VerfuegungForm = {
  /** Vorhanden, solange die Zeile aus der API stammt. */
  id?: number;
  /** Code aus `VERFUEGUNGS_ARTEN`. */
  art: string;
  /** Verknüpfter KMVS-Kontakt, sonst null. */
  bevollmaechtigterContactId: number | null;
  /** Freitextname, wenn der Bevollmächtigte nicht als Kontakt angelegt ist. */
  bevollmaechtigterName: string | null;
  /** Vom Server mitgeladener Kontakt — nur zur Anzeige, wird nicht geschrieben. */
  bevollmaechtigterContact?: { id?: number; displayName?: string; lastname?: string; firstName?: string } | null;
};

/**
 * Zeigt die Akte die Verfügungsliste? NUR bei ausdrücklichem „Ja“.
 *
 * „Nein“ und „nicht erfasst“ sehen hier gleich aus, sind es fachlich aber
 * nicht (deshalb ist das Feld dreiwertig): Bei „Nein“ ist nichts weiter zu
 * erfassen, bei „nicht erfasst“ ist noch nichts gefragt worden. Beide Male
 * gibt es nichts anzuzeigen.
 */
export const zeigeVerfuegungen = (vollmachtenVorhanden: boolean | null | undefined): boolean =>
  vollmachtenVorhanden === true;

/** API-Antwort → Formular-Zeilen, in Katalogreihenfolge. */
export function verfuegungenAusApi(rows: unknown): VerfuegungForm[] {
  const liste = Array.isArray(rows) ? rows : [];
  const nachArt = new Map<string, VerfuegungForm>();
  for (const roh of liste) {
    if (!roh || typeof roh !== "object") continue;
    const r = roh as Record<string, any>;
    const art = String(r.art ?? "").trim();
    if (!art) continue;
    nachArt.set(art, {
      ...(typeof r.id === "number" ? { id: r.id } : {}),
      art,
      bevollmaechtigterContactId:
        typeof r.bevollmaechtigterContactId === "number" ? r.bevollmaechtigterContactId : null,
      bevollmaechtigterName:
        typeof r.bevollmaechtigterName === "string" && r.bevollmaechtigterName.trim()
          ? r.bevollmaechtigterName
          : null,
      bevollmaechtigterContact: r.bevollmaechtigterContact ?? null,
    });
  }
  const bekannt = VERFUEGUNGS_ARTEN.map((a) => nachArt.get(a.code)).filter(
    (x): x is VerfuegungForm => x != null,
  );
  // Unbekannte Arten (Bestand aus einer späteren Katalogänderung) hinten dran,
  // statt sie beim nächsten Speichern stillschweigend zu löschen.
  const bekannteCodes = new Set(VERFUEGUNGS_ARTEN.map((a) => a.code));
  const rest = [...nachArt.values()].filter((v) => !bekannteCodes.has(v.art));
  return [...bekannt, ...rest];
}

/** Ist diese Art angehakt? */
export const istGewaehlt = (liste: VerfuegungForm[], art: string): boolean =>
  liste.some((v) => v.art === art);

/**
 * Haken setzen/entfernen. Entfernen löscht die Zeile mitsamt Bevollmächtigtem —
 * eine abgewählte Verfügung hat keinen.
 */
export function toggleVerfuegung(
  liste: VerfuegungForm[],
  art: string,
  an: boolean,
): VerfuegungForm[] {
  const ohne = liste.filter((v) => v.art !== art);
  if (!an) return ohne;
  if (liste.some((v) => v.art === art)) return liste;
  const naechste = [
    ...liste,
    { art, bevollmaechtigterContactId: null, bevollmaechtigterName: null },
  ];
  return sortiereNachKatalog(naechste);
}

/** Bevollmächtigten einer Zeile ändern (Kontakt-Verknüpfung ODER Freitext). */
export function setzeBevollmaechtigten(
  liste: VerfuegungForm[],
  art: string,
  patch: Partial<Pick<VerfuegungForm, "bevollmaechtigterContactId" | "bevollmaechtigterName" | "bevollmaechtigterContact">>,
): VerfuegungForm[] {
  return liste.map((v) => (v.art === art ? { ...v, ...patch } : v));
}

function sortiereNachKatalog(liste: VerfuegungForm[]): VerfuegungForm[] {
  const rang = new Map(VERFUEGUNGS_ARTEN.map((a, i) => [a.code, i]));
  return [...liste].sort(
    (a, b) => (rang.get(a.art) ?? 999) - (rang.get(b.art) ?? 999),
  );
}

/**
 * Was in der Zeile als Bevollmächtigter steht.
 *
 * Der verknüpfte Kontakt gewinnt: Sein Name ist die einzige Quelle, die einer
 * Umbenennung standhält, und nur über ihn kommt man in seine Akte. Fehlt er,
 * trägt der Freitextname — der Fall „Bevollmächtigter ist kein KMVS-Kontakt“.
 * Ist beides leer, ist die Verfügung angehakt, aber der Bevollmächtigte noch
 * offen; das ist erlaubt (der Makler kreuzt an, was der Kunde nennt, und trägt
 * den Namen nach).
 */
export function bevollmaechtigterAnzeige(v: VerfuegungForm): {
  name: string;
  contactId: number | null;
  quelle: "kontakt" | "freitext" | "offen";
} {
  const k = v.bevollmaechtigterContact;
  const kontaktName = (k ? displayContactName(k as any) : null) ?? "";
  if (v.bevollmaechtigterContactId != null && kontaktName) {
    return { name: kontaktName, contactId: v.bevollmaechtigterContactId, quelle: "kontakt" };
  }
  if (v.bevollmaechtigterContactId != null) {
    // Verknüpft, aber der Kontakt kam nicht mit (schlanker Ladepfad) — dann
    // lieber der gespeicherte Freitext als eine nackte Id.
    return {
      name: (v.bevollmaechtigterName ?? "").trim() || "Verknüpfter Kontakt",
      contactId: v.bevollmaechtigterContactId,
      quelle: "kontakt",
    };
  }
  const frei = (v.bevollmaechtigterName ?? "").trim();
  if (frei) return { name: frei, contactId: null, quelle: "freitext" };
  return { name: "", contactId: null, quelle: "offen" };
}

/** Kurzfassung für die Zusammenfassungszeile („Vorsorgevollmacht · Patientenverfügung“). */
export const verfuegungenKurz = (liste: VerfuegungForm[]): string =>
  liste.map((v) => verfuegungArtLabel(v.art)).join(" · ");

// ---------------------------------------------------------------------------
// Teil 2 — Rollen, die dieser Kontakt FÜR ANDERE ausübt
// ---------------------------------------------------------------------------

/** Eine Zeile der Liste „Dieser Kontakt vertritt“. */
export type VertretungZeile = {
  /** `ContactRelation.id` — für das Entfernen. */
  relationId: number;
  /** Gespeicherter Beziehungstyp („Betreuer“). */
  rolle: string;
  /** Beschriftung („Betreuer von“). */
  label: string;
  /** Der Vertretene. */
  gegenueberId: number | null;
  gegenueberName: string;
};

/**
 * Aus den EINGEHENDEN Beziehungen (`relationsDest`) die Vertretungen dieses
 * Kontakts herauslesen.
 *
 * Warum `relationsDest` und nicht `relationsSrc`: `relationType` beschreibt,
 * was `contactDest` für `contactSrc` ist. „Dieser Kontakt ist Betreuer“ heißt
 * also: er ist `contactDest`, der Vertretene ist `contactSrc`. Genau umgekehrt
 * zu lesen wäre die Falle, in die man hier zuerst tappt.
 */
export function vertretungenAus(relationsDest: unknown): VertretungZeile[] {
  const liste = Array.isArray(relationsDest) ? relationsDest : [];
  const zeilen: VertretungZeile[] = [];
  for (const roh of liste) {
    if (!roh || typeof roh !== "object") continue;
    const r = roh as Record<string, any>;
    const rolle = String(r.relationType?.contactRelationType ?? "").trim();
    if (!istVertretungsrolle(rolle)) continue;
    const src = r.contactSrc;
    zeilen.push({
      relationId: Number(r.id),
      rolle,
      label: vertretungsrolleLabel(rolle),
      gegenueberId: typeof src?.id === "number" ? src.id : null,
      gegenueberName: (src ? displayContactName(src) : null) ?? "—",
    });
  }
  return zeilen.sort(
    (a, b) => a.label.localeCompare(b.label, "de") || a.gegenueberName.localeCompare(b.gegenueberName, "de"),
  );
}
